import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import FinancialResponsibleForm from './FinancialResponsibleForm';
import { isValidCPF, validateBirthDateAge } from '../utils/validators';

// Ícones SVG
const Icon = ({ children, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }}>
    {children}
  </svg>
);

const LightbulbIcon = () => <Icon><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></Icon>;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${props => props.theme.colors.lightBlue};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const PhoneContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const WhatsAppCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  
  input[type="checkbox"] {
    transform: scale(1.2);
    margin: 0;
  }
  
  label {
    font-size: 14px;
    color: ${props => props.theme.colors.grayDark};
    font-weight: 500;
    cursor: pointer;
    margin: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 8px;
  
  .required {
    color: ${props => props.theme.colors.error};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.$error ? props.theme.colors.error : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.grayMedium};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.$error ? props.theme.colors.error : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  margin-top: 5px;
  display: ${props => props.show ? 'block' : 'none'};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.theme.colors.lightBlue};
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;

  svg { flex-shrink: 0; }
  strong { font-weight: 600; }
`;

const ContractChoiceGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContractChoiceOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px;
  border: 2px solid ${props => props.$selected ? props.theme.colors.secondary : '#e2e8f0'};
  border-radius: 10px;
  background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.08)' : props.theme.colors.white};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  input[type="radio"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    flex-shrink: 0;
    cursor: pointer;
    accent-color: ${props => props.theme.colors.secondary};
  }
`;

const ContractChoiceContent = styled.div`
  flex: 1;
`;

const ContractChoiceLabel = styled.span`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 4px;
`;

const ContractChoiceHint = styled.span`
  display: block;
  font-size: 13px;
  color: ${props => props.theme.colors.grayMedium};
  line-height: 1.4;
`;

const GENDER_OPTIONS = [
  { value: '', label: 'Selecione...' },
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' }
];

const PersonalDataForm = ({ onDataChange, data, financialResponsibleData, onFinancialResponsibleChange, selectedPlan }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    cpf: '',
    birthDate: '',
    gender: '',
    phone: '',
    isWhatsApp: false,
    email: '',
    contratandoParaOutro: false,
    ...data
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [birthDateErrorMsg, setBirthDateErrorMsg] = useState('O titular deve ter pelo menos 18 anos de idade');

  const isPetOnlyPlan = selectedPlan?.somentePET === true;

  useEffect(() => {
    onDataChange(formData);
  }, [formData, onDataChange]);

  useEffect(() => {
    if (isPetOnlyPlan && formData.contratandoParaOutro) {
      handleInputChange('contratandoParaOutro', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPetOnlyPlan]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Data de nascimento: validar imediatamente (não só no blur)
    if (field === 'birthDate') {
      const ageCheck = validateBirthDateAge(value, { minAge: 18, maxAge: 120, required: true });
      setBirthDateErrorMsg(ageCheck.error || 'O titular deve ter pelo menos 18 anos de idade');
      setFieldErrors(prev => ({
        ...prev,
        birthDate: !ageCheck.valid
      }));
      return;
    }

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const applyMask = (field, value) => {
    switch (field) {
      case 'cpf':
        value = value.replace(/\D/g, '');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        return value;
      
      case 'phone':
        value = value.replace(/\D/g, '');
        // Limitar a 11 dígitos (DDD + 9 dígitos do celular)
        value = value.substring(0, 11);
        // Aplicar máscara
        if (value.length <= 10) {
          // Telefone fixo: (XX) XXXX-XXXX
          value = value.replace(/(\d{2})(\d)/, '($1) $2');
          value = value.replace(/(\d{4})(\d)/, '$1-$2');
        } else {
          // Celular: (XX) XXXXX-XXXX
          value = value.replace(/(\d{2})(\d)/, '($1) $2');
          value = value.replace(/(\d{5})(\d)/, '$1-$2');
        }
        return value;
      
      default:
        return value;
    }
  };

  const handleMaskedInputChange = (field, value) => {
    const maskedValue = applyMask(field, value);
    handleInputChange(field, maskedValue);
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
      
      case 'cpf':
        return isValidCPF(value);
      
      case 'phone':
        const phoneRegex = /^\(\d{2}\) \d{4,5}-\d{4}$/;
        return phoneRegex.test(value);
      
      case 'birthDate':
        return validateBirthDateAge(value, { minAge: 18, maxAge: 120, required: true }).valid;
      
      default:
        return value.trim() !== '';
    }
  };

  const handleBlur = (field) => {
    if (field === 'birthDate') {
      const ageCheck = validateBirthDateAge(formData.birthDate, {
        minAge: 18,
        maxAge: 120,
        required: true,
      });
      setBirthDateErrorMsg(ageCheck.error || 'O titular deve ter pelo menos 18 anos de idade');
      setFieldErrors(prev => ({
        ...prev,
        birthDate: !ageCheck.valid
      }));
      return;
    }

    const isValid = validateField(field, formData[field]);
    setFieldErrors(prev => ({
      ...prev,
      [field]: !isValid
    }));
  };

  return (
    <>
      <SectionTitle>Dados Pessoais</SectionTitle>

      {!isPetOnlyPlan && (
        <>
          <ContractChoiceGroup role="radiogroup" aria-label="Para quem é o plano">
            <ContractChoiceOption $selected={!formData.contratandoParaOutro}>
              <input
                type="radio"
                name="contratandoParaOutro"
                checked={!formData.contratandoParaOutro}
                onChange={() => handleInputChange('contratandoParaOutro', false)}
              />
              <ContractChoiceContent>
                <ContractChoiceLabel>O plano é para mim</ContractChoiceLabel>
                <ContractChoiceHint>
                  Você será o titular (beneficiário) do plano e também o responsável financeiro.
                </ContractChoiceHint>
              </ContractChoiceContent>
            </ContractChoiceOption>
            <ContractChoiceOption $selected={formData.contratandoParaOutro}>
              <input
                type="radio"
                name="contratandoParaOutro"
                checked={formData.contratandoParaOutro}
                onChange={() => handleInputChange('contratandoParaOutro', true)}
              />
              <ContractChoiceContent>
                <ContractChoiceLabel>Contratarei o plano para outra pessoa</ContractChoiceLabel>
                <ContractChoiceHint>
                  Você será o responsável pelo plano, incluindo as <b>responsabilidades administrativas e financeiras</b>. "Os dados do titular (beneficiário) deverão ser informados abaixo" (quem possui a cobertura do plano).
                </ContractChoiceHint>
              </ContractChoiceContent>
            </ContractChoiceOption>
          </ContractChoiceGroup>

          {formData.contratandoParaOutro && onFinancialResponsibleChange && (
            <FinancialResponsibleForm
              onDataChange={onFinancialResponsibleChange}
              data={financialResponsibleData}
            />
          )}
        </>
      )}

      <SectionTitle>Dados do Titular</SectionTitle>

      <FormGroup>
        <Label htmlFor="fullName">
          Nome Completo <span className="required">*</span>
        </Label>
        <Input
          type="text"
          id="fullName"
          value={formData.fullName}
          onChange={(e) => handleInputChange('fullName', e.target.value)}
          onBlur={() => handleBlur('fullName')}
          $error={fieldErrors.fullName}
          placeholder="Digite seu nome completo"
          required
        />
        <ErrorMessage show={fieldErrors.fullName}>
          Por favor, informe seu nome completo
        </ErrorMessage>
      </FormGroup>

      <FormRow>
        <FormGroup>
          <Label htmlFor="cpf">
            CPF <span className="required">*</span>
          </Label>
          <Input
            type="text"
            id="cpf"
            value={formData.cpf}
            onChange={(e) => handleMaskedInputChange('cpf', e.target.value)}
            onBlur={() => handleBlur('cpf')}
            placeholder="000.000.000-00"
            $error={fieldErrors.cpf}
            maxLength="14"
            required
          />
          <ErrorMessage show={fieldErrors.cpf}>
            Por favor, informe um CPF válido
          </ErrorMessage>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="birthDate">
            Data de Nascimento <span className="required">*</span>
          </Label>
          <Input
            type="date"
            id="birthDate"
            value={formData.birthDate}
            onChange={(e) => handleInputChange('birthDate', e.target.value)}
            onBlur={() => handleBlur('birthDate')}
            $error={fieldErrors.birthDate}
            max={new Date().toISOString().split('T')[0]}
            min={new Date(new Date().setFullYear(new Date().getFullYear() - 120)).toISOString().split('T')[0]}
            required
          />
          <ErrorMessage show={fieldErrors.birthDate}>
            {birthDateErrorMsg}
          </ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="gender">
            Sexo <span className="required">*</span>
          </Label>
          <Select
            id="gender"
            value={formData.gender}
            onChange={(e) => handleInputChange('gender', e.target.value)}
            onBlur={() => handleBlur('gender')}
            $error={fieldErrors.gender}
            required
          >
            {GENDER_OPTIONS.map(gender => (
              <option key={gender.value} value={gender.value}>
                {gender.label}
              </option>
            ))}
          </Select>
          <ErrorMessage show={fieldErrors.gender}>
            Por favor, selecione o sexo
          </ErrorMessage>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="phone">
            Telefone <span className="required">*</span>
          </Label>
          <PhoneContainer>
            <Input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleMaskedInputChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              placeholder="(21) 99999-9999"
              maxLength={15}
              $error={fieldErrors.phone}
              required
            />
            <WhatsAppCheckbox>
              <input
                type="checkbox"
                id="isWhatsApp"
                checked={formData.isWhatsApp}
                onChange={(e) => handleInputChange('isWhatsApp', e.target.checked)}
              />
              <label htmlFor="isWhatsApp">
                Este é um número de WhatsApp
              </label>
            </WhatsAppCheckbox>
          </PhoneContainer>
          <ErrorMessage show={fieldErrors.phone}>
            Por favor, informe um telefone válido
          </ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormGroup>
        <Label htmlFor="email">
          E-mail <span className="required">*</span>
        </Label>
        <Input
          type="email"
          id="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          onBlur={() => handleBlur('email')}
          placeholder="seu@email.com"
          $error={fieldErrors.email}
          required
        />
        <ErrorMessage show={fieldErrors.email}>
          Por favor, informe um e-mail válido
        </ErrorMessage>
      </FormGroup>

      <InfoBox>
        <LightbulbIcon /> Informe um e-mail ou marque o WhatsApp — precisamos de ao menos um contato.
      </InfoBox>
    </>
  );
};

export default PersonalDataForm;

